// src/pages/Register.js (drop-in replacement)
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { registerWithEmail } from '../utils/authService';
import '../assets/styles/Login.css';
import BloomPal_Logo from '../assets/images/BloomPal_MainLogo.png';
import Blossom from '../assets/images/Blossom.webp';
import Google_g from '../assets/images/google-g.svg';
import api from '../utils/api';
import UserContext from '../context/UserContext';

function Register() {
  const navigate = useNavigate();
  const { setName, setReason } = useContext(UserContext);

  // —— Form state
  const [email, setEmail] = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // —— IDs we pass through to backend
  const visitorId = localStorage.getItem('visitorId') || '';
  const sessionId = localStorage.getItem('sessionId') || '';

  // —— reCAPTCHA site key from env (prefers Vite VITE_, falls back to CRA REACT_APP_)
  const siteKey =
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_RECAPTCHA_SITE_KEY) ||
    process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
    ''; // <- set this in your env

  // Track script load status to avoid duplicate inserts
  const recaptchaLoadedRef = useRef(false);

  // ——————————————————————————————————————————
  // Utilities
  // ——————————————————————————————————————————
  const getCsrfToken = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith(name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const fetchUserProfile = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const res = await api.get('/api/auth/user/', {
        headers: { Authorization: `Token ${token}` },
      });
      if (res.data) {
        setName(res.data.name || '');
        setReason(res.data.reason || '');
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  // Load reCAPTCHA v3 script and wait until it's ready
  const loadRecaptcha = () =>
    new Promise((resolve, reject) => {
      if (!siteKey) {
        return reject(new Error('Missing RECAPTCHA site key env var'));
      }
      if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
        return resolve(window.grecaptcha);
      }
      if (recaptchaLoadedRef.current) {
        // already injected; poll briefly for ready()
        const iv = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.ready) {
            clearInterval(iv);
            resolve(window.grecaptcha);
          }
        }, 50);
        setTimeout(() => {
          clearInterval(iv);
          if (window.grecaptcha && window.grecaptcha.ready) resolve(window.grecaptcha);
          else reject(new Error('reCAPTCHA failed to initialize'));
        }, 5000);
        return;
      }
      recaptchaLoadedRef.current = true;
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          resolve(window.grecaptcha);
        } else {
          reject(new Error('reCAPTCHA ready() not available'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
      document.head.appendChild(script);
    });

  const getRecaptchaToken = async (action) => {
    const grecaptcha = await loadRecaptcha();
    return new Promise((resolve, reject) => {
      grecaptcha.ready(async () => {
        try {
          const token = await grecaptcha.execute(siteKey, { action });
          resolve(token);
        } catch (err) {
          reject(err);
        }
      });
    });
  };

  // Preload CSRF + reCAPTCHA
  useEffect(() => {
    // Pre-warm CSRF
    api.get('/set-csrf/').catch((e) => console.warn('Failed to prefetch CSRF:', e?.message || e));
    // Preload reCAPTCHA script (non-blocking)
    loadRecaptcha().catch(() => {
      // not fatal until we submit
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ——————————————————————————————————————————
  // Handlers
  // ——————————————————————————————————————————
  const handleRegister = async () => {
    setErrorMsg('');

    if (!email) return setErrorMsg('Email is required.');
    if (password1.length < 8) return setErrorMsg('Password must be at least 8 characters.');
    if (password1 !== password2) return setErrorMsg('Passwords do not match.');

    setLoading(true);
    try {
      const captchaToken = await getRecaptchaToken('register');

      await registerWithEmail(
        email.trim(),
        password1,
        password2,
        visitorId,
        captchaToken,
        sessionId
      );

      await fetchUserProfile();
      navigate('/');
    } catch (e) {
      console.error('Registration error:', e?.response?.data || e?.message || e);
      const msg =
        e?.response?.data?.email?.[0] ||
        e?.response?.data?.password1?.[0] ||
        e?.response?.data?.password2?.[0] ||
        e?.response?.data?.non_field_errors?.[0] ||
        e?.response?.data?.detail ||
        'Registration failed. Please try again.';
      setErrorMsg(msg);
      // Optional: refresh reCAPTCHA after failure
      try { await getRecaptchaToken('register'); } catch (_) {}
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const name = localStorage.getItem('guestName') || '';
      const reason = localStorage.getItem('userReason') || '';
      const response = await api.post(
        '/api/auth/google/',
        {
          id_token: credentialResponse.credential,
          visitor_id: visitorId,
          session_id: sessionId,
          name,
          reason,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
        }
      );
      localStorage.setItem('authToken', response.data.key);
      await fetchUserProfile();
      navigate('/');
    } catch (error) {
      console.error('Google registration failed:', error?.response?.data || error?.message || error);
      let msg = 'Google registration failed. Please try again.';
      if (error?.response?.data?.error) msg = error.response.data.error;
      else if (error?.response?.status === 400) msg = 'Invalid Google authentication. Please try again.';
      else if (error?.response?.status === 500) msg = 'Server error. Please try again later.';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google registration failed:', error);
    setErrorMsg('Google registration failed. Please try again.');
    setLoading(false);
  };

  // ——————————————————————————————————————————
  // Render
  // ——————————————————————————————————————————
  return (
    <div className="login-container">
      <img src={BloomPal_Logo} alt="BloomPal Logo" className="login-logo" />
      <div className="login-content">
        <div className="login-left">
          <img src={Blossom} alt="Mascot" className="mascot" />
          <h1 className="welcome-text">Welcome to our family!</h1>
        </div>

        <div className="login-right">
          <div className="login-card">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            <label htmlFor="pw1">Password</label>
            <input
              id="pw1"
              type="password"
              placeholder="Password"
              value={password1}
              onChange={(e) => setPassword1(e.target.value)}
              disabled={loading}
            />

            <label htmlFor="pw2">Confirm Password</label>
            <input
              id="pw2"
              type="password"
              placeholder="Confirm Password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              disabled={loading}
            />

            <button
              className="login-button"
              onClick={handleRegister}
              disabled={loading || !siteKey}
              aria-busy={loading}
            >
              {loading ? 'Processing…' : 'Sign Up'}
            </button>

            {errorMsg && <p className="error-text" role="alert">{errorMsg}</p>}

            <div className="divider"><span>OR</span></div>

            <div className="google-login-wrapper" style={{ width: '100%' }}>
              {loading ? (
                <button className="social google" disabled>
                  <img src={Google_g} alt="Google" className="google-icon" />
                  Processing...
                </button>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="continue_with"
                  shape="rectangular"
                />
              )}
            </div>

            <p className="signup-text">
              Already have an account? <Link to="/login"><strong>LOG IN</strong></Link>
            </p>

            {!siteKey && (
              <p className="tiny-note">
                reCAPTCHA not configured. Set <code>VITE_RECAPTCHA_SITE_KEY</code> or <code>REACT_APP_RECAPTCHA_SITE_KEY</code>.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
